import React from 'react';
import { 
  SimpleGrid, 
  Button, 
  VStack, 
  Box, 
  useColorModeValue,
  useBreakpointValue,
  Tooltip,
  Flex,
  Text
} from '@chakra-ui/react';
import { 
  UploadSimple, 
  Scan, 
  CheckCircle, 
  Image as ImageIcon,
  MapPin
} from '@phosphor-icons/react';
import SubLocationSelector from './SubLocationSelector';
import { useSiteData } from '../../hooks/useSiteData';

const MediaUploadActions = ({ 
  fileInputRef,
  imageFile,
  isLoading,
  isUploaded,
  detections,
  onSelectClick,
  onScanClick,
  onUploadClick,
  siteId,
  selectedSubLocationId,
  onSubLocationSelect,
  accentColor
}) => {
  const { site, loading: siteLoading } = useSiteData(siteId);
  
  // Responsive layout
  const buttonSize = useBreakpointValue({ base: "md", md: "lg" });
  const iconSize = useBreakpointValue({ base: 20, md: 24 });
  const gridColumns = useBreakpointValue({ base: 1, md: 3 });
  
  // Theme colors
  const buttonBg = useColorModeValue('gray.100', 'gray.700');
  const buttonHoverBg = useColorModeValue('gray.200', 'gray.600');
  const disabledBg = useColorModeValue('gray.50', 'gray.800');
  const disabledColor = useColorModeValue('gray.400', 'gray.500');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <VStack spacing={6} w="100%">
      <SimpleGrid columns={gridColumns} spacing={4} w="100%">
        <Button
          onClick={onSelectClick}
          colorScheme="blue"
          variant="outline"
          size={buttonSize}
          height="90px"
          isDisabled={isLoading}
          borderRadius="md"
          transition="all 0.2s"
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: 'md'
          }}
        >
          <VStack spacing={2}>
            <ImageIcon size={iconSize} weight="light" color={accentColor} />
            <Text fontSize="sm" fontWeight="medium">Select Image</Text>
          </VStack>
        </Button>

        <Button
          onClick={onScanClick}
          colorScheme={imageFile ? "teal" : "gray"}
          isDisabled={!imageFile || isLoading}
          size={buttonSize}
          height="90px"
          borderRadius="md"
          bg={!imageFile || isLoading ? disabledBg : undefined}
          color={!imageFile || isLoading ? disabledColor : undefined}
          transition="all 0.2s"
          _hover={{
            transform: imageFile && !isLoading ? 'translateY(-2px)' : 'none',
            boxShadow: imageFile && !isLoading ? 'md' : 'none'
          }}
        >
          <VStack spacing={2}>
            <Scan size={iconSize} weight={imageFile ? "light" : "thin"} />
            <Text fontSize="sm" fontWeight="medium">
              {imageFile ? "Analyze Image" : "No Image Selected"}
            </Text>
          </VStack>
        </Button>

        <Button
          onClick={onUploadClick}
          colorScheme={detections.length > 0 ? "green" : "gray"}
          isDisabled={!imageFile || isLoading || isUploaded}
          size={buttonSize}
          height="90px"
          borderRadius="md"
          bg={!imageFile || isLoading || isUploaded ? disabledBg : undefined}
          color={!imageFile || isLoading || isUploaded ? disabledColor : undefined}
          transition="all 0.2s"
          _hover={{
            transform: imageFile && !isLoading && !isUploaded && detections.length > 0 ? 'translateY(-2px)' : 'none',
            boxShadow: imageFile && !isLoading && !isUploaded && detections.length > 0 ? 'md' : 'none'
          }}
        >
          <VStack spacing={2}>
            <UploadSimple size={iconSize} weight={isUploaded ? "fill" : "light"} />
            <Text fontSize="sm" fontWeight="medium">
              {isUploaded ? "Uploaded" : "Upload Image"}
            </Text>
          </VStack>
        </Button>
      </SimpleGrid>
      
      {imageFile && (
        <Box 
          w="100%" 
          p={4} 
          borderRadius="md" 
          bg={buttonBg}
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Flex align="center" mb={2} gap={2}>
            <MapPin size={18} color={accentColor} />
            <Text fontSize="sm" fontWeight="medium" color={textColor}>
              Add Location (Optional)
            </Text>
          </Flex>
          
          <SubLocationSelector
            siteId={siteId}
            currentSubLocationId={selectedSubLocationId}
            onSelect={onSubLocationSelect}
            disabled={isLoading || isUploaded}
            siteLocation={!siteLoading ? site?.location : null}
          />
        </Box>
      )}
    </VStack>
  );
};

export default MediaUploadActions;
